import { useEffect, useCallback, useState } from 'react';
import { X, ChevronLeft, ChevronRight, Plus, Minus, RotateCcw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

export function Lightbox({ images, currentIndex, onClose, onNavigate }) {
  const [zoom, setZoom] = useState(1);
  const currentImage = images[currentIndex];
  const primaryImageSource = currentImage?.url || currentImage?.thumbnail_url;
  const [resolvedImageSource, setResolvedImageSource] = useState(primaryImageSource);

  useEffect(() => {
    setResolvedImageSource(primaryImageSource);
  }, [primaryImageSource]);

  useEffect(() => {
    setZoom(1);
  }, [currentIndex]);

  const handleZoomIn = useCallback(() => {
    setZoom((prev) => Math.min(MAX_ZOOM, Number((prev + ZOOM_STEP).toFixed(2))));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => Math.max(MIN_ZOOM, Number((prev - ZOOM_STEP).toFixed(2))));
  }, []);

  const resetZoom = useCallback(() => {
    setZoom(1);
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') onNavigate('prev');
    if (e.key === 'ArrowRight') onNavigate('next');
    if (e.key === '+' || e.key === '=') handleZoomIn();
    if (e.key === '-') handleZoomOut();
    if (e.key === '0') resetZoom();
  }, [onClose, onNavigate, handleZoomIn, handleZoomOut, resetZoom]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [handleKeyDown]);

  if (!currentImage) return null;

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < images.length - 1;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
        onClick={onClose}
        data-testid="lightbox-overlay"
      >
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-4 right-4 z-20 text-white/80 hover:text-white hover:bg-white/10"
          onClick={onClose}
          aria-label="Close"
          data-testid="lightbox-close"
        >
          <X className="h-7 w-7" />
        </Button>

        <div
          className="absolute top-4 left-4 md:left-6 z-20 flex items-center gap-1.5 rounded-lg bg-black/85 border border-white/15 p-1"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-white hover:bg-white/10"
            onClick={handleZoomOut}
            disabled={zoom <= MIN_ZOOM}
            aria-label="Zoom out"
            data-testid="lightbox-zoom-out"
          >
            <Minus className="h-4 w-4" />
          </Button>
          <span className="text-xs text-white min-w-[46px] text-center tabular-nums">{Math.round(zoom * 100)}%</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-white hover:bg-white/10"
            onClick={handleZoomIn}
            disabled={zoom >= MAX_ZOOM}
            aria-label="Zoom in"
            data-testid="lightbox-zoom-in"
          >
            <Plus className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-white hover:bg-white/10"
            onClick={resetZoom}
            aria-label="Reset zoom"
            data-testid="lightbox-zoom-reset"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>

        {hasPrev && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-3 md:left-6 z-20 text-white/80 hover:text-white hover:bg-white/10 h-12 w-12"
            onClick={(e) => { e.stopPropagation(); onNavigate('prev'); }}
            aria-label="Previous image"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="h-8 w-8" />
          </Button>
        )}

        {hasNext && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-3 md:right-6 z-20 text-white/80 hover:text-white hover:bg-white/10 h-12 w-12"
            onClick={(e) => { e.stopPropagation(); onNavigate('next'); }}
            aria-label="Next image"
            data-testid="lightbox-next"
          >
            <ChevronRight className="h-8 w-8" />
          </Button>
        )}

        <motion.div
          key={currentImage.id || primaryImageSource || currentIndex}
          initial={{ scale: 0.98, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.98, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="w-full h-full flex items-center justify-center p-4 md:p-8"
          onClick={(e) => e.stopPropagation()}
        >
          {resolvedImageSource ? (
            <div
              className="max-w-[96vw] max-h-[94vh] overflow-auto rounded-lg [&::-webkit-scrollbar]:hidden"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              data-testid="lightbox-image-container"
            >
              <img
                src={resolvedImageSource}
                alt={currentImage.title || 'Gallery image'}
                className="w-auto h-auto max-w-[96vw] max-h-[94vh] object-contain rounded-lg shadow-2xl transition-transform duration-200 ease-out"
                style={{ transform: `scale(${zoom})`, transformOrigin: 'center center' }}
                onError={() => {
                  if (currentImage?.thumbnail_url && resolvedImageSource !== currentImage.thumbnail_url) {
                    setResolvedImageSource(currentImage.thumbnail_url);
                  } else {
                    setResolvedImageSource(null);
                  }
                }}
                data-testid="lightbox-image"
              />
            </div>
          ) : (
            <div className="text-white/70 text-sm" data-testid="lightbox-image-missing">
              Image unavailable. Please try another image.
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}